import * as CL from '@design-system/component-library';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { DialogType } from '../../common/enums.js';
import { useSubmitReview } from '../../common/hooks/useSubmitReview';
import { t } from '../../common/i18n/index.js';
import { PostReviewRequest } from '../../generated/api/models.js';
import { openDialog } from '../../public/site/dialogStore.js';
import type { CustomerOrderDetailsFormValues } from './CustomerOrderDetails.js';
import type { ReviewOnlineOrderDialogParams } from '../../common/types/dialog.js';
import type { ContactHeader, CustomerOrder } from '../../generated/api/models.js';

import './CustomerOrderDetails.scss';

interface CustomerOrderFooterParams {
  customerOrder: CustomerOrder;
  ordererContact?: ContactHeader;
  isChecked?: boolean;
  onTriggerMandatoryCheckboxError: () => void;
}

export const CustomerOrderFooter = ({
  customerOrder,
  ordererContact,
  isChecked,
  onTriggerMandatoryCheckboxError,
}: CustomerOrderFooterParams) => {
  const { submitReview } = useSubmitReview(true);
  const { getValues, setError } = useFormContext<CustomerOrderDetailsFormValues>();
  const [isSaving, setIsSaving] = React.useState(false);

  const getReviewOnlineOrderDialogParams = (approved: boolean): ReviewOnlineOrderDialogParams => {
    const formValues = getValues();
    const onlineOrderReview = {
      billingAccountId: formValues.billingAccountId,
      costCenter: formValues.costCenter,
      subscriptionReference: formValues.employeeNumber,
      onlineOrderId: customerOrder?.onlineOrderId || '',
    };
    return {
      onlineOrderReview,
      approved,
      customerOrder: customerOrder,
      type: DialogType.REVIEW_ONLINE_ORDER,
      ordererEmail: ordererContact?.email,
    };
  };

  const showApproveOrRejectDialog = (e: React.SyntheticEvent) => {
    const target = e.currentTarget.id || '';

    if (target === 'reject') {
      openDialog(getReviewOnlineOrderDialogParams(false));
    } else if (target === 'approve') {
      const values = getValues();
      if (!values.billingAccountId) {
        setError('billingAccountId', { type: 'custom', message: t.GMD4('The billing account must be chosen') });
        return;
      } else if (!isChecked) {
        onTriggerMandatoryCheckboxError();
        return;
      }

      const onlineOrderReview = {
        billingAccountId: values.billingAccountId,
        costCenter: values.costCenter,
        subscriptionReference: values.employeeNumber,
        onlineOrderId: customerOrder?.onlineOrderId || '',
      };

      setIsSaving(true);

      void submitReview(onlineOrderReview, PostReviewRequest.ReviewTypeEnum.ONLINE_ORDER, true);
    }
  };

  return (
    <div className="of-customer-order-details__sticky-footer">
      <div className="of-customer-order-details__sticky-footer__buttons">
        <CL.Button size="l" color="light" onClick={showApproveOrRejectDialog} disabled={isSaving} id="reject">
          {t.ONSX('Reject')}
        </CL.Button>

        <CL.Button
          size="l"
          className="of-customer-order-details__order-action"
          onClick={showApproveOrRejectDialog}
          id="approve"
          loading={isSaving}
        >
          {t.P5RQ('Accept')}
        </CL.Button>
      </div>
    </div>
  );
};
